import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as api from '../../../services/api';
import { Button, Pill } from '../';
import { Pagination, usePaginationState } from '../../common/Pagination';
import { useMarketplaceCatalogue } from '../../../data/marketplaceRegistry';
import { findMarketplaceForInstalled } from '../../../data/marketplaceFromRegistry';
import { Trash2 } from 'lucide-react';

/**
 * ExteletConnectorSkills — inline, in-place picker for a connected data
 * connector's actions on the ExteLet Build surface. When the agent runs on
 * "Live connector", each connected connector's advertised actions are shown
 * here; the operator ticks the ones to expose and clicks "Add selected" to turn
 * them into governed connector-bound skills. Added actions can be removed any
 * time. Destructive actions start unticked.
 *
 * Pure reuse: connector actions come from the shared marketplace catalogue
 * (useMarketplaceCatalogue → findMarketplaceForInstalled), the same source the
 * native skill picker uses; skills are created/removed through the same
 * /agents/{id}/skills endpoints (addSkill + linkSkillConnector + deleteSkill);
 * paging uses the shared Pagination component. No connector-specific fork.
 *
 * @param {Object}   props.connector  the connected platform connector (id, name, type, url)
 * @param {Object}   props.agent      the ExteLet agent (needs id)
 * @param {Function} props.addToast
 * @param {Function} props.onChanged  called after add/remove so the parent can refresh gate/bindings
 */
const DESTRUCTIVE_RE = /delete|purge|truncate|drop|execute|apex|anonymous|bulk/i;
function isDestructive(a) {
  const method = (a.method || a.httpMethod || 'GET').toUpperCase();
  return method === 'DELETE'
    || DESTRUCTIVE_RE.test(a.name || a.displayName || '')
    || DESTRUCTIVE_RE.test(a.endpoint || a.path || '');
}
const actionName = (a) => a.name || a.displayName || a.id || '';

export default function ExteletConnectorSkills({ connector, agent, addToast, onChanged }) {
  const { connectors: marketplaceConnectors = [] } = useMarketplaceCatalogue();
  const marketplace = useMemo(
    () => (connector ? findMarketplaceForInstalled(marketplaceConnectors, connector) : null),
    [marketplaceConnectors, connector],
  );
  const actions = useMemo(
    () => (marketplace && Array.isArray(marketplace.skills) ? marketplace.skills : []),
    [marketplace],
  );

  // name → skillId for the connector actions already added as skills on this agent.
  const [addedByName, setAddedByName] = useState({});
  const [staged, setStaged] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const { page, pageSize, setPage, setPageSize } = usePaginationState(10);

  const loadAdded = useCallback(async () => {
    if (!agent?.id) return;
    try {
      const res = await api.getAgentSkills(agent.id);
      const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      const byName = {};
      list.filter((s) => s.skillType === 'connector_skill')
          .forEach((s) => { byName[s.name] = s.id; });
      setAddedByName(byName);
    } catch { /* keep last-known state on a transient read failure */ }
  }, [agent?.id]);
  useEffect(() => { loadAdded(); }, [loadAdded]);

  const toggle = (name) => setStaged((prev) => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });
  const selectableNames = useMemo(
    () => actions.map(actionName).filter((n) => n && !addedByName[n]),
    [actions, addedByName],
  );
  const selectAll = () => setStaged(new Set(selectableNames));
  const selectNone = () => setStaged(new Set());

  const addSelected = async () => {
    if (staged.size === 0 || !agent?.id || !connector) return;
    setBusy(true);
    const chosen = actions.filter((a) => staged.has(actionName(a)));
    let ok = 0; let failed = 0;
    for (const op of chosen) {
      const endpoint = op.endpoint || op.path || '';
      const method = (op.method || op.httpMethod || 'GET').toUpperCase();
      const displayName = op.displayName || op.name || op.id || '';
      const payload = {
        name: op.name || displayName,
        description: `${op.description || displayName} [${method} ${endpoint}]`,
        category: 'connectors',
        riskLevel: op.riskLevel || (isDestructive(op) ? 'high' : 'low'),
        autonomy: 'HUMAN_CONTROLLED',
        inferenceMode: 'PLATFORM_INFERENCE',
        skillType: 'connector_skill',
        endpoint: endpoint || null,
        httpMethod: method,
        baseUrl: marketplace?.baseUrl || connector.url || null,
        params: op.params || null,
        credentialType: op.credentialType || marketplace?.authType || connector.authMethod || null,
        requiredScopes: op.requiredScopes || null,
        samplePayload: op.samplePayload || null,
        accessDescription: op.accessDescription || `Calls ${method} ${endpoint} on ${connector.name}`,
        // Required by the skills table (NOT NULL). Off by default: a governed call returns
        // to the agent rather than completing server-side. Matches the native picker default.
        serverExecuteEnabled: false,
      };
      try {
        const res = await api.addSkill(agent.id, payload);
        if (res && res.ok === false) { failed += 1; continue; }
        const created = res?.data || res;
        const sid = created?.id || created?.skillId;
        if (sid) {
          await api.linkSkillConnector(agent.id, sid, { connectorId: connector.id, purpose: 'data_source' })
            .catch(() => null);
        }
        ok += 1;
      } catch { failed += 1; }
    }
    setBusy(false);
    setStaged(new Set());
    await loadAdded();
    onChanged?.();
    // Truthful, outcome-specific messaging: never claim skills were added when none were.
    if (ok > 0 && failed === 0) {
      addToast?.({
        tone: 'success',
        title: `Added ${ok} tool${ok === 1 ? '' : 's'}`,
        description: `${connector.name} action${ok === 1 ? ' is' : 's are'} now a governed skill on this agent.`,
      });
    } else if (ok > 0 && failed > 0) {
      addToast?.({
        tone: 'warning',
        title: `Added ${ok}, ${failed} couldn’t be added`,
        description: `The added ${connector.name} action${ok === 1 ? ' is' : 's are'} now governed. Try the remaining ${failed} again in a moment.`,
      });
    } else {
      addToast?.({
        tone: 'danger',
        title: `Couldn’t add ${failed} action${failed === 1 ? '' : 's'}`,
        description: `No changes were made to ${connector.name}. Please try again in a moment.`,
      });
    }
  };

  const removeSkill = async (name) => {
    const sid = addedByName[name];
    if (!sid || !agent?.id) return;
    setBusy(true);
    try { await api.deleteSkill(agent.id, sid); } finally { setBusy(false); }
    await loadAdded();
    onChanged?.();
    addToast?.({ tone: 'success', title: 'Tool removed', description: name });
  };

  if (!connector) return null;
  if (!marketplace || actions.length === 0) {
    return (
      <p className="text-[11.5px] text-v2-ink-muted mb-2 max-w-[560px]">
        {connector.name} advertises no actions to add. Your governance policy still applies to
        every governed call this agent makes.
      </p>
    );
  }

  const addedCount = Object.keys(addedByName).filter((n) => actions.some((a) => actionName(a) === n)).length;
  const pageActions = actions.slice(page * pageSize, (page + 1) * pageSize);

  return (
    <div className="border border-v2-border-subtle rounded-v2-sm mb-2 max-w-[560px] overflow-hidden">
      <div className="flex items-center justify-between px-2 py-1.5 bg-v2-surface border-b border-v2-border-subtle">
        <span className="text-[11px] uppercase tracking-wider font-semibold text-v2-ink-muted">
          Actions from {connector.name}
        </span>
        <span className="flex items-center gap-2">
          <span className="text-[11px] text-v2-ink-muted">{addedCount} added · {actions.length} available</span>
          <Button variant="ghost" size="sm" disabled={busy || selectableNames.length === 0} onClick={selectAll}>
            Select all
          </Button>
          <Button variant="ghost" size="sm" disabled={busy || staged.size === 0} onClick={selectNone}>
            None
          </Button>
        </span>
      </div>

      <ul className="m-0 p-0 list-none">
        {pageActions.map((a) => {
          const name = actionName(a);
          const added = Boolean(addedByName[name]);
          const method = (a.method || a.httpMethod || 'GET').toUpperCase();
          return (
            <li key={name}
                className="flex items-center gap-2 px-2 py-1.5 border-b border-v2-border-subtle">
              <input
                type="checkbox"
                className="shrink-0"
                checked={added || staged.has(name)}
                disabled={added || busy}
                onChange={() => toggle(name)}
                aria-label={`Select ${name}`}
              />
              <Pill tone="muted" size="sm">{method}</Pill>
              <span className="flex-1 min-w-0 truncate">
                <span className="text-[12.5px] font-medium text-v2-ink">{name}</span>
                {(a.endpoint || a.path) && (
                  <span className="text-[10.5px] text-v2-ink-muted font-mono-v2 ml-1">{a.endpoint || a.path}</span>
                )}
              </span>
              {isDestructive(a) && <Pill tone="danger" size="sm">destructive</Pill>}
              {added
                ? (
                  <Button variant="ghost" size="sm" iconLeft={<Trash2 size={12} />}
                    disabled={busy} onClick={() => removeSkill(name)}>
                    Remove
                  </Button>
                )
                : <Pill tone="muted" size="sm">not added</Pill>}
            </li>
          );
        })}
      </ul>

      <Pagination
        totalItems={actions.length}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        noun="actions"
      />

      <div className="flex items-center justify-between gap-2 px-2 py-1.5 bg-v2-surface border-t border-v2-border-subtle">
        <span className="text-[11px] text-v2-ink-muted">
          {staged.size > 0 ? `${staged.size} selected` : 'Tick actions to add as governed skills'}
        </span>
        <Button variant="primary" size="sm" disabled={staged.size === 0 || busy} loading={busy}
          onClick={addSelected}>
          {busy ? 'Adding…' : `Add selected${staged.size ? ` (${staged.size})` : ''}`}
        </Button>
      </div>
    </div>
  );
}
